import {UmpireImage} from '../umpire-image';
import {UmpireOttEventKind} from '../umpire-ott-event-kind';
import {UmpireOttFormat} from '../umpire-ott-format';
import {UmpireOttSegment} from '../umpire-ott-segment';
import {UmpirePublicationOptions} from '../umpire-publication-options';
import {UmpireTag} from '../umpire-tag';
import {UmpireOttEventStatus} from '../umpire-ott-event-status';
import {Language} from '../umpire-language-service';

export type PpvCurrencyCode = 'EUR' | 'USD' | 'PLN';

export interface PpvPrice {
  currency_code: PpvCurrencyCode;
  /**
   * Price in cents/smallest currency unit
   * @example 1050 // represents 10.50 EUR/USD/PLN
   * @example 1 // represents 0.01
   */
  price: number;
}

/**
 * A single entry of the PPV price schedule (moderator-only `ppv` attribute).
 * Exactly one entry with `date_from: null` represents the base price.
 */
export interface PpvPriceScheduleEntry {
  date_from: string | null;
  prices: PpvPrice[];
}

/**
 * `prices` relationship of the event (requires `?include=prices`).
 * `external_id` is the Stripe price_id sent to checkout.
 */
export interface UmpireOttEventPrice {
  type: 'price';
  id: string;
  external_id: string;
  /** Price in cents/smallest currency unit */
  price: number;
  currency_code: PpvCurrencyCode;
  resources: string[];
  external_name: string;
  locale: string;
  relationshipNames?: string[];
}

export class UmpireOttEvent implements UmpirePublicationOptions {
  title!: string;

  description!: string;

  teaser_copy!: string;

  author!: string;

  kind!: UmpireOttEventKind;

  format!: UmpireOttFormat;

  segment!: UmpireOttSegment | null;

  type!: 'event';

  id!: string;

  external_id!: string;

  slug!: string | null;

  image!: UmpireImage;

  image_vertical!: UmpireImage;

  image_horizontal!: UmpireImage;

  tags!: UmpireTag[];

  stream!: UmpireOttStream;

  external_event_status!: UmpireOttEventStatus;

  key!: string;

  push_url!: string;

  is_featured!: boolean;

  use_backup!: boolean;

  relationshipNames!: string[];

  start_time!: string | null;

  publication_time!: string | null;

  published_at!: string | null;

  is_draft!: boolean;

  external_file_upload_path!: string | null;

  metadata!: UmpireOttEventMetadata;

  provider!: 'bitmovin';

  start_stream_automatically!: boolean;

  locale!: Language;

  available_translations!: Language[];

  protocol!: UmpireOttStreamProtocol;

  /** 10–79 chars. Create-only — ignored by the API on update. */
  srt_passphrase?: string;

  /**
   * Broadcaster's listener address (`srt://host:port[?passphrase=…&streamid=…&latency=…]`).
   * Its presence switches the event to SRT CALLER mode; empty means LISTENER.
   * Create-only — ignored by the API on update.
   */
  srt_target_url?: string;

  /** AES key length in bits. Create-only — ignored by the API on update. */
  srt_aes_variant?: UmpireOttSrtAesVariant;

  /**
   * CALLER only — connect from a static IP the broadcaster can whitelist.
   * Create-only — ignored by the API on update.
   */
  srt_static_ip?: boolean;

  encryption!: UmpireOttStreamEncryption | undefined;

  showAds!: boolean;

  /**
   * Entitlements resource of a PPV event (e.g. `fightingspirit:live:123`).
   * Pass verbatim to the entitlements inquiry.
   */
  entitlement_resource?: string;

  /** Moderator-only PPV price schedule. */
  ppv?: PpvPriceScheduleEntry[];

  /** Resolved from the `prices` relationship (requires `?include=prices`). */
  prices?: UmpireOttEventPrice[];
}

export interface UmpireOttStream {
  type: 'stream';
  id: string;
  key: string;
  push_url: string;
  push_url_backup?: string;
  title: string;
  external_id: string;
  active: boolean;
  enabled: boolean;
  public: boolean;
  published: boolean;
  kind: 'TYPE_LIVE' | 'TYPE_STATIC';
  backup_key: string | null;
  backup_enabled: boolean;
  manifests?: UmpireOttStreamManifests | null;
  started_at: string | null;
  marker?: UmpireOttStreamMarker;
  download_url: string;
  download_status: StreamDownloadStatus;
  protocol: UmpireOttStreamProtocol;
  thumbnails?: {url: string};
  srt_input?: UmpireOttStreamSrtInput | null;
}

/** SRT connection details. The passphrase is intentionally never returned. */
export interface UmpireOttStreamSrtInput {
  mode: UmpireOttStreamSrtMode;
  host: string | null;
  port: string | null;
  stream_id: string | null;
  latency: string | number | null;
  /** Filled only with `srt_static_ip: true` — the IP the broadcaster must whitelist. */
  source_ip_address: string | null;
}

/**
 * LISTENER — we listen and the broadcaster pushes to our `push_url`.
 * CALLER — the broadcaster listens and we connect to its `srt_target_url`
 * (no `push_url`, no backup).
 */
export enum UmpireOttStreamSrtMode {
  LISTENER = 'listener',
  CALLER = 'caller',
}

export type UmpireOttSrtAesVariant = 128 | 192 | 256;

export enum StreamDownloadStatus {
  FAILED = 'failed',
  FINISHED = 'finished',
  NOT_STARTED = 'not_started',
  STARTED = 'started',
}

export interface UmpireOttStreamManifests {
  [UmpireOttStreamManifestsType.hls]: string;
  [UmpireOttStreamManifestsType.dash]: string;
}

export interface UmpireOttEventMetadata {
  contentType: string;
  id: number;
  season: string;
  tags: string[];
  title: string;
  tvShow: string;
}

export enum UmpireOttStreamManifestsType {
  hls = 'hls',
  dash = 'dash',
}

export type UmpireOttStreamManifestsKeys = keyof typeof UmpireOttStreamManifestsType;

export interface UmpireOttStreamMarker {
  id?: number;
  items: UmpireOttStreamMarkerItem[];
  stream?: Partial<UmpireOttStream>;
  relationshipNames: string[];
  type: 'marker';
}

export interface UmpireOttStreamMarkerItem {
  time: number;
  label: string;
}

export enum UmpireOttStreamProtocol {
  SRT = 'srt',
  RTMP = 'rtmp',
}

export enum UmpireOttStreamEncryption {
  AES128 = 'AES-128',
  AES192 = 'AES-192',
  AES256 = 'AES-256',
  NONE = 'none',
}
